import { FormItemProps } from 'antd'
import { ArrayElement } from '@/common/types/misc.def.ts'
import { NewOfficeLocationBuildingOptions, OfficeLocation } from '@/common/types/work-info.def.ts'
import { get } from 'lodash-es'
import { SelectGroupOption, SelectOption } from '@/common/types/components.def.ts'

export type AntdFormRule = ArrayElement<FormItemProps['rules']>

const requiredRule: AntdFormRule = { required: true, message: 'Required' }
const requiredIfRule: (arg: boolean) => AntdFormRule = (x) => ({ required: x, message: 'Required' })

// create a function which accepts an optional object with a boolean property `allowZero`

type ValidPercentOptions = { allowZero?: boolean }
const validPercent: (options?: ValidPercentOptions) => AntdFormRule = (options = {}) => () => ({
  validator(_, value) {
    if (value != undefined) {
      if (value > 1) return Promise.reject(new Error('Should not be larger than 100'))
      if (!options.allowZero && value <= 0) return Promise.reject(new Error('Should be strictly larger than 0'))
      if (options.allowZero && value < 0) return Promise.reject(new Error('Should not be smaller than 0'))
    }

    return Promise.resolve()
  },
})

const validOfficeCity: AntdFormRule = ({ getFieldValue }) => ({
  validator(_, value) {
    const city = value
    const country = getFieldValue('country')

    if (!value || get(OfficeLocation, [country, city])) {
      return Promise.resolve()
    }
    return Promise.reject(new Error('Invalid City'))
  },
})

const validOfficeCode: AntdFormRule = ({ getFieldValue }) => ({
  validator(_, value) {
    const code = value
    const city = getFieldValue(['officeLocation', 'city']) as string

    if (NewOfficeLocationBuildingOptions(city)?.some(x => x.value === code)) {
      return Promise.resolve()
    }
    return Promise.reject(new Error('Invalid Office Code'))
  },
})

const validSelectedValue: (allowOptions: (SelectOption | SelectGroupOption) []) => AntdFormRule = (allowOptions) => () => ({
  validator(_, value) {
    const allowedValues = allowOptions.map(x => {
      if ('options' in x) {
        return x.options.map(opt => opt.value)
      } else {
        return x.value
      }
    }).flat() // flat for group options
    if (value) {
      if (!Array.isArray(value)) {
        if (!allowedValues.includes(value)) {
          return Promise.reject(new Error('Invalid value'))
        }
      } else {
        const invalidValues = (value as string[]).filter(val => !allowedValues.includes(val))
        if (invalidValues.length > 0) {
          return Promise.reject(new Error(`Invalid values: ${invalidValues.join(', ')}`))
        }
      }
    }

    return Promise.resolve()
  },
})

const maxItems: (maxCount: number) => AntdFormRule = (maxCount) => () => ({
  validator(_, value) {
    if (value != undefined && Array.isArray(value) && value.length > maxCount) {
      return Promise.reject(new Error(`Max items count: ${maxCount}`))
    }

    return Promise.resolve()
  },
})

export const AntdFormRules = {
  required: requiredRule,
  requiredIf: requiredIfRule,
  maxItems,
  validOfficeCity,
  validOfficeCode,
  validPercent,
  validSelectedValue,
}